import { get, isNil } from 'lodash';
import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import IconMC from './IconMC';
import {
  formatCurrencyTyping,
  formatMoney,
  parseCurrencyToRaw,
  sanitizeDecimalInput,
  sanitizeIntegerInput
} from '../../util/numberUtils';
import TooltipMC from './TooltipMC';

const FloatingLabelTopPx = 5;
const FloatingLabelGapBelowPx = 4;
/** Matches former `tw-pt-5` so single-line labels keep the same typing position. */
const FloatingLabelSingleLinePaddingTopPx = 20;
/** Above ~1 line of `text-xs` + `leading-snug`; use measured padding to clear wrapped labels. */
const FloatingLabelMultiLineHeightThresholdPx = 28;

const buildFloatingShellClassNames = ({ hideBorder, extra = [] }) => [
  'tw-block',
  'tw-rounded-xl',
  'tw-w-full',
  'tw-overflow-hidden',
  'tw-bg-gray-50',
  'tw-border-gray-200',
  'tw-appearance-none',
  'focus-within:tw-outline-none',
  'focus-within:tw-ring-2',
  'focus-within:tw-ring-inset',
  'focus-within:tw-ring-blue-500',
  hideBorder ? '' : 'tw-border',
  ...extra
].filter(Boolean).join(' ');

const InputComponent = ({
  label,
  placeholder,
  className,
  id,
  ariaLabel,
  onChange,
  onInput,
  onBlur,
  value,
  name,
  secondaryLabel,
  maxLength,
  min,
  max,
  type = 'text',
  textarea = false,
  rows = 3,
  useFloatingLabel = false,
  useFloatingAndNonFloatingLabel = false,
  containerClass = '',
  floatingLabelClassName = '',
  customError,
  icon = null,
  iconSize = 'base',
  hideBorder = false,
  disabled = false,
  showPasswordToggle = false,
  required = false,
  currency = false,
  decimal = false,
  integer = false,
  rules = {},
  labelTooltip
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [currencyFocused, setCurrencyFocused] = useState(false);
  const [currencyDisplayValue, setCurrencyDisplayValue] = useState('');
  const floatingLabelRef = useRef(null);
  const [floatingLabelHeightPx, setFloatingLabelHeightPx] = useState(null);
  const isPasswordType = type === 'password';
  const currentType = isPasswordType && showPasswordToggle && showPassword ? 'text' : type;
  const inputId = id || name;
  const errorId = `${name}-error`;
  const {
    control,
    formState: { errors },
    trigger,
    setValue,
    getValues
  } = useFormContext();

  useEffect(() => {
    if (!currency || currencyFocused) return;

    if (value !== undefined && value !== null && value !== '') {
      setCurrencyDisplayValue(formatMoney(value));
      return;
    }

    setCurrencyDisplayValue('');
  }, [currency, currencyFocused, value]);

  const onChangeValue = (event, fieldChange) => {
    if (disabled) return;

    fieldChange(event);
    if (onChange) {
      onChange(id || name, event.target.value);
    }
    if (get(errors, name)) {
      trigger(name);
    }
  };

  const onCurrencyChange = (event, fieldChange) => {
    if (disabled) return;

    const formattedValue = formatCurrencyTyping(event.target.value);
    const rawValue = parseCurrencyToRaw(formattedValue);

    setCurrencyDisplayValue(formattedValue);

    const syntheticEvent = {
      ...event,
      target: {
        ...event.target,
        value: rawValue,
        name: event.target.name || name
      }
    };

    fieldChange(syntheticEvent);
    if (onChange) {
      onChange(id || name, rawValue);
    }
    if (get(errors, name)) {
      trigger(name);
    }
  };

  const onDecimalChange = (event, fieldChange) => {
    if (disabled) return;

    const rawValue = sanitizeDecimalInput(event.target.value);

    const syntheticEvent = {
      ...event,
      target: {
        ...event.target,
        value: rawValue,
        name: event.target.name || name
      }
    };

    fieldChange(syntheticEvent);
    if (onChange) {
      onChange(id || name, rawValue);
    }
    if (get(errors, name)) {
      trigger(name);
    }
  };

  const onIntegerChange = (event, fieldChange) => {
    if (disabled) return;

    const rawValue = sanitizeIntegerInput(event.target.value);

    const syntheticEvent = {
      ...event,
      target: {
        ...event.target,
        value: rawValue,
        name: event.target.name || name
      }
    };

    fieldChange(syntheticEvent);
    if (onChange) {
      onChange(id || name, rawValue);
    }
    if (get(errors, name)) {
      trigger(name);
    }
  };

  const onCurrencyFocus = () => {
    setCurrencyFocused(true);
  };

  const onCurrencyBlur = (event, fieldBlur) => {
    setCurrencyFocused(false);

    if (String(currencyDisplayValue.trim()) !== '') {
      const rawValue = parseCurrencyToRaw(currencyDisplayValue);
      setCurrencyDisplayValue(formatMoney(rawValue));
    }

    if (fieldBlur) fieldBlur(event);
    if (onBlur) onBlur(event);
  };

  const onInputValue = (event, fieldChange) => {
    if (disabled) return;

    fieldChange(event);
    if (onInput) {
      onInput(event);
    }
    if (get(errors, name)) {
      trigger(name);
    }
  };

  const showLabel = (!useFloatingLabel || useFloatingAndNonFloatingLabel) && label;
  const showFloatingLabel = useFloatingLabel || useFloatingAndNonFloatingLabel;
  const isColorType = type === 'color';
  const isTextareaWithFloatingLabel = textarea && showFloatingLabel;

  const inputColorTypeClassNames = [
    'tw-px-2',
    'tw-py-1',
    'tw-bg-white',
    'tw-rounded-lg',
    'tw-appearance-none',
    'focus:tw-outline-none',
    'focus:tw-ring-2',
    'focus:tw-ring-inset',
    'focus:tw-ring-blue-400',
    'tw-text-sm',
    'tw-border',
    'tw-w-full',
    'tw-h-[42px]',
    className || ''
  ].join(' ');

  const getRightPadding = () => {
    if (showPasswordToggle && isPasswordType) return 'tw-pr-12';
    return 'tw-pr-4';
  };

  const getLeftPadding = () => {
    if (icon) return 'tw-pl-12';
    if (hideBorder) return 'tw-pl-1';
    return 'tw-pl-4';
  };

  const inputClassNames = [
    'tw-block',
    'tw-rounded-xl',
    getLeftPadding(),
    getRightPadding(),
    showFloatingLabel ? 'tw-pb-0' : 'tw-py-3',
    'tw-w-full',
    'tw-text-default',
    'tw-bg-gray-50',
    'tw-border-gray-200',
    'tw-appearance-none',
    'focus:tw-outline-none',
    'focus:tw-ring-2',
    'focus:tw-ring-inset',
    'focus:tw-ring-blue-500',
    hideBorder ? '' : 'tw-border',
    showFloatingLabel ? 'tw-text-left placeholder:tw-text-left' : '',
    textarea ? 'tw-placeholder-gray-500 tw-resize-none' : '',
    className || ''
  ].join(' ');

  const floatingLabelClassNames = [
    'tw-absolute',
    'tw-block',
    'tw-text-xs',
    'tw-text-blue-500',
    'tw-top-[5px]',
    'tw-z-10',
    'tw-leading-snug',
    icon ? 'tw-start-12' : 'tw-start-4',
    showPasswordToggle && isPasswordType ? 'tw-end-12' : 'tw-end-4',
    labelTooltip ? 'tw-flex tw-items-center tw-gap-1' : 'tw-pointer-events-none',
    floatingLabelClassName || ''
  ].join(' ');

  const textareaFloatingShellClassNames = buildFloatingShellClassNames({ hideBorder });

  const textareaFloatingInputClassNames = [
    'tw-block',
    'tw-w-full',
    getLeftPadding(),
    getRightPadding(),
    'tw-pt-0',
    'tw-pb-3',
    'tw-text-default',
    'tw-bg-transparent',
    'tw-border-0',
    'tw-resize-none',
    'tw-appearance-none',
    'focus:tw-outline-none',
    'tw-placeholder-gray-500',
    'placeholder:tw-text-left',
    className || ''
  ].join(' ');

  useLayoutEffect(() => {
    if (!showFloatingLabel) {
      setFloatingLabelHeightPx(null);
      return undefined;
    }

    const el = floatingLabelRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const updateHeight = () => {
      setFloatingLabelHeightPx(Math.ceil(el.getBoundingClientRect().height));
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, [showFloatingLabel, label, secondaryLabel]);

  useEffect(() => {
    if (!isNil(value) && getValues(name) !== value) {
      setValue(name, value);
    }
  }, [value, setValue, name, getValues]);

  const getFieldChangeHandler = (fieldChange) => {
    if (currency) return (e) => onCurrencyChange(e, fieldChange);
    if (decimal) return (e) => onDecimalChange(e, fieldChange);
    if (integer) return (e) => onIntegerChange(e, fieldChange);
    return (e) => onChangeValue(e, fieldChange);
  };

  const labelClasses = 'tw-text-zinc-700 tw-mb-1 tw-text-base tw-font-bold';
  const colorLabelClasses = 'tw-text-xs tw-text-blue-500 tw-mb-0.5';

  return (
    <Controller
      name={name}
      control={control}
      defaultValue=""
      rules={rules}
      render={({ field }) => {
        const { ref, onBlur: fieldOnBlur } = field;
        const fieldError = get(errors, name);
        const hasError = Boolean(fieldError?.message) || Boolean(customError);
        const resolvedValue = field.value !== undefined ? field.value : value;

        let floatingPaddingTopPx = null;
        if (showFloatingLabel && !isColorType) {
          const isMultiLineLabel = floatingLabelHeightPx != null
            && floatingLabelHeightPx > FloatingLabelMultiLineHeightThresholdPx;
          floatingPaddingTopPx = isMultiLineLabel
            ? FloatingLabelTopPx + floatingLabelHeightPx + FloatingLabelGapBelowPx
            : FloatingLabelSingleLinePaddingTopPx;
        }

        const describedByIds = [
          fieldError?.message ? errorId : null,
          customError ? `${errorId}-custom` : null
        ].filter(Boolean).join(' ');

        const accessibilityProps = {
          id: inputId,
          disabled,
          required,
          'aria-invalid': hasError,
          ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
          ...(describedByIds ? { 'aria-describedby': describedByIds } : {})
        };

        const sharedFieldProps = {
          placeholder,
          maxLength,
          onChange: getFieldChangeHandler(field.onChange),
          onInput: currency || decimal || integer ? undefined : (e) => onInputValue(e, field.onChange),
          ...(currency && {
            onFocus: onCurrencyFocus,
            onBlur: (e) => onCurrencyBlur(e, fieldOnBlur)
          }),
          ...((decimal || integer) && !currency && { onBlur: fieldOnBlur }),
          value: currency ? currencyDisplayValue : resolvedValue
        };

        const inputProps = {
          ...sharedFieldProps,
          ...accessibilityProps,
          ref,
          name,
          min,
          max,
          className: isColorType ? inputColorTypeClassNames : inputClassNames,
          type: currency || decimal || integer ? 'text' : currentType,
          inputMode: currency || decimal ? 'decimal' : (integer ? 'numeric' : undefined),
          ...(floatingPaddingTopPx != null && !isTextareaWithFloatingLabel
            ? { style: { paddingTop: `${floatingPaddingTopPx}px` } }
            : {})
        };

        const textareaProps = {
          ...(currency || decimal || integer ? { ref, name } : field),
          ...sharedFieldProps,
          ...accessibilityProps,
          className: inputClassNames,
          rows,
          ...(floatingPaddingTopPx != null
            ? { style: { paddingTop: `${floatingPaddingTopPx}px` } }
            : {})
        };

        const fieldProps = currency || decimal || integer ? { ref, name } : field;

        let fieldElement;
        if (isTextareaWithFloatingLabel) {
          fieldElement = (
            <div
              className={textareaFloatingShellClassNames}
              style={floatingPaddingTopPx != null ? { paddingTop: `${floatingPaddingTopPx}px` } : undefined}
            >
              {/* eslint-disable react/jsx-props-no-spreading */}
              <textarea
                {...field}
                {...sharedFieldProps}
                {...accessibilityProps}
                className={textareaFloatingInputClassNames}
                rows={rows}
              />
              {/* eslint-enable react/jsx-props-no-spreading */}
            </div>
          );
        } else if (textarea) {
          // eslint-disable-next-line react/jsx-props-no-spreading
          fieldElement = <textarea {...fieldProps} {...textareaProps} />;
        } else {
          // eslint-disable-next-line react/jsx-props-no-spreading
          fieldElement = <input {...fieldProps} {...inputProps} />;
        }

        return (
          <div className={`tw-wrapper ${containerClass}`}>
            {showLabel && (
              <div className="tw-flex tw-items-center tw-gap-1 tw-mb-1">
                <label htmlFor={inputId} className={`${isColorType ? colorLabelClasses : labelClasses} tw-mb-0`}>
                  {label}
                </label>
                {labelTooltip && (
                  <TooltipMC text={labelTooltip} position="right">
                    <IconMC name="info-outline" size="xs" className="tw-text-blue-500 tw-flex-shrink-0 tw-cursor-help" />
                  </TooltipMC>
                )}
              </div>
            )}
            <div className="tw-relative">
              {icon && (
                <div className="tw-absolute tw-left-4 tw-top-1/2 tw--translate-y-1/2 tw-z-10 tw-pointer-events-none">
                  <IconMC name={icon} size={iconSize} className="tw-text-gray-400" />
                </div>
              )}
              {fieldElement}
              {showFloatingLabel && (label || secondaryLabel) && (
                <div ref={floatingLabelRef} className={floatingLabelClassNames}>
                  <label
                    htmlFor={inputId}
                    className="tw-mb-0 tw-pointer-events-none"
                  >
                    {label}
                    {secondaryLabel && (
                      <span className="tw-text-xs tw-text-gray-500 tw-ml-1">{secondaryLabel}</span>
                    )}
                  </label>
                  {labelTooltip && (
                    <TooltipMC text={labelTooltip} position="right">
                      <IconMC name="info-outline" size="xs" className="tw-text-blue-500 tw-flex-shrink-0 tw-cursor-help" />
                    </TooltipMC>
                  )}
                </div>
              )}
              {showPasswordToggle && isPasswordType && (
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="tw-absolute tw-right-4 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 hover:tw-text-gray-600 tw-cursor-pointer tw-z-10 tw-bg-transparent tw-border-none tw-p-0"
                  tabIndex={-1}
                >
                  <IconMC name={showPassword ? 'eye-outline' : 'eye-fill'} size="base" />
                </button>
              )}
            </div>
            {fieldError?.message && (
              <p id={errorId} className="tw-text-xs tw-text-red-500">
                {fieldError.message}
              </p>
            )}
            {customError && (
              <p id={`${errorId}-custom`} className="tw-text-xs tw-text-red-500">
                {customError}
              </p>
            )}
          </div>
        );
      }}
    />
  );
};

export default InputComponent;
